import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { Icon, ProfilePicture } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { Button, H5, H6, IconType } from "@sellar-io/components";
import { PageIndicator } from "../../components/pageIndicator/PageIndicator";
import { Switch } from "@sellar-io/components";
import { DISCOVER } from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useBuyerUpdate } from "../../hooks/api/v2/retailer/useBuyerUpdate";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import {
  OwnershipType,
  BuyerBusinessOwnership,
} from "./BuyerBusinessOwnership";
import { BuyerVenueSelection } from "./BuyerVenueSelection";
import { BuyerVenueSelectOther } from "./BuyerVenueSelectOther";
import { BuyerVenueSteps } from "./BuyerVenueTypeForm.shared";

export function BuyerVenueTypeForm() {
  const { business } = useSellarCurrentBusiness();
  const [step, setStep] = useState<BuyerVenueSteps>(
    BuyerVenueSteps.VenueTypeStep,
  );
  const [selectedVenueTypes, setSelectedVenueTypes] = useState<string[]>();
  const businessVenueTypes = business?.Retailer?.venueTypes;
  const [prevBusinessVenueTypes, setPrevBusinessVenueTypes] =
    useState(businessVenueTypes);

  // Sync selected venue types when business data arrives/changes.
  if (businessVenueTypes !== prevBusinessVenueTypes) {
    setPrevBusinessVenueTypes(businessVenueTypes);
    if (businessVenueTypes) {
      setSelectedVenueTypes(businessVenueTypes);
    }
  }
  return (
    <>
      {step.name === BuyerVenueSteps.VenueTypeStep.name && (
        <BuyerVenueTypeStep
          setStep={setStep}
          setSelectedVenueTypes={setSelectedVenueTypes}
          selectedVenueTypes={selectedVenueTypes}
        />
      )}
      {step.name === BuyerVenueSteps.VenueTypeStepOther.name && (
        <BuyerVenueTypeOtherStep
          setStep={setStep}
          setSelectedVenueTypes={setSelectedVenueTypes}
          selectedVenueTypes={selectedVenueTypes}
        />
      )}
      {step.name === BuyerVenueSteps.OwnershipTypeStep.name && (
        <BuyerOwnershipTypeStep
          setStep={setStep}
          selectedVenueTypes={selectedVenueTypes}
        />
      )}
      {step.name === BuyerVenueSteps.ConfirmationStep.name && (
        <BuyerVenueConfirmationStep />
      )}
    </>
  );
}

const BuyerVenueTypeStep = ({
  setStep,
  selectedVenueTypes,
  setSelectedVenueTypes,
}: {
  selectedVenueTypes?: string[];
  setSelectedVenueTypes: (selectedVenueTypes?: string[]) => void;
  setStep: (step: BuyerVenueSteps) => void;
}) => {
  return (
    <div className={"flex flex-col gap-3 text-center"}>
      <H5>Tell us about your business</H5>
      <H6>
        This helps us to provide you with the best experience on Sellar. Choose{" "}
        <strong>up to three</strong> that apply.
      </H6>
      <div className="items-center">
        <BuyerVenueSelection
          selectedVenueTypes={selectedVenueTypes}
          setSelectedVenueTypes={setSelectedVenueTypes}
        />
      </div>
      <BuyerVenueFormButtons
        pageLength={2}
        activePageIndex={0}
        buttonText={"NEXT"}
        icon={"CHEVRON_RIGHT"}
        onClick={() =>
          selectedVenueTypes?.includes("other")
            ? setStep(BuyerVenueSteps.VenueTypeStepOther)
            : setStep(BuyerVenueSteps.OwnershipTypeStep)
        }
      />
    </div>
  );
};

const BuyerVenueTypeOtherStep = ({
  setStep,
  selectedVenueTypes,
  setSelectedVenueTypes,
}: {
  selectedVenueTypes?: string[];
  setSelectedVenueTypes: (selectedVenueTypes?: string[]) => void;
  setStep: (step: BuyerVenueSteps) => void;
}) => {
  const form = useForm({
    defaultValues: {
      venueType: null,
    },
  });
  const venueType = useWatch({
    control: form.control,
    name: "venueType",
  });
  return (
    <>
      <div className={"mb-3 flex flex-col gap-3"}>
        <div className={"flex flex-col gap-2 text-center"}>
          <H5>Tell us about your business</H5>
          <H6>
            This helps us to provide you with the best experience on Sellar.
          </H6>
        </div>
        <BuyerVenueSelectOther form={form} />
      </div>
      <BuyerVenueFormButtons
        pageLength={2}
        activePageIndex={0}
        buttonText={"NEXT"}
        icon={"CHEVRON_RIGHT"}
        disabled={!venueType}
        step={BuyerVenueSteps.VenueTypeStepOther}
        setStep={setStep}
        onClick={() => {
          if (venueType) {
            setSelectedVenueTypes([
              ...(selectedVenueTypes
                ? selectedVenueTypes.filter((type) => type !== "other")
                : []),
              venueType,
            ]);
          }
          setStep(BuyerVenueSteps.OwnershipTypeStep);
        }}
      />
    </>
  );
};

const BuyerOwnershipTypeStep = ({
  setStep,
  selectedVenueTypes,
}: {
  selectedVenueTypes?: string[];
  setStep: (step: BuyerVenueSteps) => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  if (!business?.Retailer) throw new Error("Only buyers can edit this form");
  const [ownershipType, setOwnershipType] = useState<OwnershipType | null>(
    business.Retailer.ownershipType,
  );

  const buyerUpdateMutation = useBuyerUpdate(business.Retailer.id);
  const onSubmit = () => {
    buyerUpdateMutation.mutate(
      {
        venueTypes: selectedVenueTypes,
        ownershipType: ownershipType,
      },
      {
        onSuccess: () => setStep(BuyerVenueSteps.ConfirmationStep),
      },
    );
  };
  return (
    <>
      <div className={"mb-3 flex flex-col gap-3"}>
        <div className="flex flex-col gap-2 text-center">
          <H5>Tell us about your business</H5>
          <H6>How would you describe the ownership of your business?</H6>
        </div>
        {buyerUpdateMutation.isError ? (
          <Alert
            type={"error"}
            message={
              apiV2AssertError(buyerUpdateMutation.error)?.response?.data
                .message ?? "Unknown error"
            }
          />
        ) : null}
        <BuyerBusinessOwnership
          value={ownershipType}
          onChange={(event) =>
            setOwnershipType(event.currentTarget.value as OwnershipType)
          }
        />
      </div>
      <BuyerVenueFormButtons
        pageLength={2}
        activePageIndex={1}
        buttonText={"Finish"}
        onClick={onSubmit}
      />
    </>
  );
};

const BuyerVenueConfirmationStep = () => {
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const buyerUpdateMutation = useBuyerUpdate(business?.Retailer?.id ?? 0);
  const optedIn = business?.Retailer?.hasOptedInOffersAndUpdates ?? false;
  const [checked, setChecked] = useState<boolean>(optedIn);
  const [prevOptedIn, setPrevOptedIn] = useState(optedIn);
  const isOrderRoute = window.location.pathname.includes("order");

  // Sync opt-in switch when business data arrives/changes.
  if (optedIn !== prevOptedIn) {
    setPrevOptedIn(optedIn);
    setChecked(optedIn);
  }

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div
          className={twMerge(
            "flex flex-row gap-2",
            isOrderRoute && "flex-col text-center",
          )}
        >
          <div className="flex justify-center">
            <ProfilePicture logoUrl={business?.logoUrl} name={business?.name} />
            <Icon
              size={0.75}
              icon={"CHECK_CIRCLE"}
              className={twMerge(
                "mt-auto -ml-4 rounded-full bg-white text-green-500",
                !isOrderRoute && "mt-6",
              )}
            />
          </div>
          <div className={twMerge("flex flex-col", isOrderRoute && "gap-2")}>
            <H5>Thank you for updating your business details</H5>
            <p>
              We are working toward providing you with a more personalised
              experience on Sellar.
            </p>
          </div>
        </div>
        <div
          className={
            "w-full rounded-lg border border-zinc-300 bg-zinc-50 p-3 text-sm"
          }
        >
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1">
              <H5>Stay in the loop</H5>
              <H6>Opt in for news, offers and updates from Sellar.</H6>
            </div>
            <Switch
              checked={checked}
              onChange={() => {
                setChecked(!checked);
                buyerUpdateMutation.mutate(
                  {
                    hasOptedInOffersAndUpdates: !checked,
                  },
                  {
                    onError: () => setChecked(checked),
                  },
                );
              }}
            />
          </div>
        </div>
        {/*<Alert*/}
        {/*  type="sellar"*/}
        {/*  title={"What would you like to see from Sellar?"}*/}
        {/*  message={*/}
        {/*    "You can share yor feedback with the Sellar team by filling in this short survey"*/}
        {/*  }*/}
        {/*  className={"mb-4 w-full border-zinc-300 bg-zinc-50"}*/}
        {/*>*/}
        {/*  <Button*/}
        {/*    onClick={() => window.open("", "_blank")}*/}
        {/*    color={"tertiary"}*/}
        {/*    className={"pl-0 text-blue-700"}*/}
        {/*  >*/}
        {/*    <Icon icon={"OPEN_IN_NEW"} />*/}
        {/*    Complete Survey*/}
        {/*  </Button>*/}
        {/*</Alert>*/}
      </div>
      {isOrderRoute && (
        <div className="mt-2 mt-auto w-full">
          <Button className={"w-full"} onClick={() => navigate(DISCOVER)}>
            Continue Browsing
          </Button>
        </div>
      )}
    </>
  );
};
const BuyerVenueFormButtons = ({
  pageLength,
  activePageIndex,
  buttonText,
  onClick,
  icon,
  disabled,
  step,
  setStep,
}: {
  pageLength: number;
  activePageIndex: number;
  buttonText: string;
  onClick: () => void;
  icon?: IconType;
  disabled?: boolean;
  step?: BuyerVenueSteps;
  setStep?: (step: BuyerVenueSteps) => void;
}) => {
  const isOtherOptionStep =
    step?.name === BuyerVenueSteps.VenueTypeStepOther.name;
  return (
    <div className="mt-auto grid w-full grid-cols-3">
      <div className="col-span-1">
        {isOtherOptionStep && (
          <Button
            color={"tertiary"}
            onClick={() => setStep?.(BuyerVenueSteps.VenueTypeStep)}
          >
            <Icon icon={"CHEVRON_LEFT"} />
            Back
          </Button>
        )}
      </div>
      <div className="align-center col-span-1 flex items-center justify-center">
        <PageIndicator
          pageLength={pageLength}
          activePageIndex={activePageIndex}
        />
      </div>
      <div className={twMerge("col-span-1 flex justify-end")}>
        <Button color={"tertiary"} disabled={disabled} onClick={onClick}>
          {buttonText} {icon && <Icon icon={icon} />}
        </Button>
      </div>
    </div>
  );
};
